/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * WHICH TIME COUNTS AS "THE LAST ANALYSIS" FOR THE NEXT ACTION (v1_757).
 *
 * Row 7 of the Next action ("Reprocess with the client's new information") fires
 * when the client's activity (last_update_at) is newer than the last FULL
 * analysis. The builder was fed report.riskAssessment.computedAt as that anchor,
 * but computedAt is rewritten by EVERY engine run, not only a full analysis: the
 * retier that screening webhooks, identity outcomes and the client app itself
 * fire. The client app fires one on every chat turn once an escalation flag is
 * present (App.jsx callClaude, /api/risk-assess), right after save-message has
 * stamped last_update_at. So on a return visit (upload, then a few chat turns)
 * the anchor landed about a second after the client's own stamp and row 7 could
 * never fire (end to end walk run 6: last client turn stamped 00:30:37.7, the
 * turn's retier rewrote computedAt at about 00:30:39.4; after Check responses
 * the Next action went straight to "Review 4 client answers").
 *
 * The anchor for row 7 is therefore the marker only a full assembly writes: the
 * analysis strip's 'complete' entry, written by df-process at the end of the
 * first pass, the submission run and every Reprocess (the consistency sweep that
 * follows keeps its time). No new store: the strip is already on the session.
 *
 * Row 13 (officer board change since the last refresh) is unchanged: its anchor
 * is the later of the analysis and the last Quick Update, and an entity type pin
 * or manual re-tier clears it by moving computedAt. computedAt is folded into the
 * refresh time handed to the builder so row 13 reads exactly as before.
 *
 * A case with no 'complete' marker (the standard interview, which has no
 * analysis strip, or a documents first run that never completed) keeps
 * computedAt, the previous anchor, and says so in `rowSevenAnchor`.
 */

function ms(t) {
  if (!t) return 0;
  const n = new Date(t).getTime();
  return Number.isFinite(n) ? n : 0;
}

export function completeMarkerAt(analysisProgress) {
  if (!Array.isArray(analysisProgress)) return null;
  const done = analysisProgress.filter(e => e && e.key === 'complete' && e.status === 'done' && ms(e.at) > 0);
  if (!done.length) return null;
  return done.reduce((a, b) => (ms(b.at) > ms(a.at) ? b : a)).at;
}

export function nextActionAnchors({ report, analysisProgress, lastQuickUpdateAt, aiReviewAt } = {}) {
  const computedAt = (report && report.riskAssessment && report.riskAssessment.computedAt) || null;
  const marker = completeMarkerAt(analysisProgress);
  const lastAnalysisAt = marker || computedAt;
  const refresh = [lastQuickUpdateAt, computedAt].filter(t => ms(t) > 0);
  return {
    lastAnalysisAt,
    lastQuickUpdateAt: refresh.length ? refresh.reduce((a, b) => (ms(b) > ms(a) ? b : a)) : null,
    rowSevenAnchor: marker ? 'full-analysis' : (computedAt ? 'engine-run' : 'none'),
    // v1_819: row 13 anchors on the AI review's OWN time (its generatedAt, which
    // Quick Update and the Adversarial Review stamp, or the Quick Update stamp).
    // A re tier rewrites computedAt without refreshing the review, so counting it
    // cleared the Quick Update row a screening resolution or an acknowledgement
    // had raised (walk w9723ddf6). Null when the case has no AI review yet: row
    // 13 then keeps the engine anchor.
    reviewAnchorAt: (() => { const t = [lastQuickUpdateAt, aiReviewAt].filter(x => ms(x) > 0); return t.length ? t.reduce((a, b) => (ms(b) > ms(a) ? b : a)) : null; })(),
  };
}
